import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {AppState} from 'store/customer/storeSetup';

interface InitialStateInterface {
    productDataLoading: boolean;
    isRailOnFlat: boolean;
    isRailOnEdge: boolean;
    carcaseThickness: number;
    railWidth: number;
    applianceOpeningHeight: number;
    productType: number;
    productUpdated: boolean;
    searchText?: string;
    searchingProduct: boolean;
    popovers: number[];
    errorPopovers: number[];
}

const initialState: InitialStateInterface = {
    productDataLoading: false,
    isRailOnFlat: false,
    isRailOnEdge: false,
    carcaseThickness: 0,
    railWidth: 0,
    applianceOpeningHeight: 0,
    productType: 0,
    productUpdated: false,
    searchingProduct: false,
    popovers: [],
    errorPopovers: [],
};

const productSlice = createSlice({
    name: 'productData',
    initialState,
    selectors: {
        // something new I discovered just recently, cleaner as we don't have to import AppState anymore
        selectErrorPopovers: (state) => state.errorPopovers,
        selectPopovers: (state) => state.popovers,
    },
    reducers: {
        removePopover: (state, {payload}: PayloadAction<number>) => {
            const index = state.popovers.indexOf(payload);
            const errorIndex = state.errorPopovers.indexOf(payload);

            if (errorIndex == -1) {
                if (index > -1) {
                    state.popovers.splice(index, 1);
                }
            }
        },
        addPopover: (state, {payload}: PayloadAction<number>) => {
            state.popovers.push(payload);
        },
        removeErrorPopover: (state, {payload}: PayloadAction<number>) => {
            const index = state.errorPopovers.indexOf(payload);

            if (index > -1) {
                state.errorPopovers.splice(index, 1);
            }
        },
        addErrorPopover: (state, {payload}: PayloadAction<number>) => {
            state.errorPopovers.push(payload);
        },
        setSearchingProduct: (state, {payload}: PayloadAction<boolean>) => {
            state.searchingProduct = payload;
        },
        setSearchText: (
            state,
            {payload}: PayloadAction<string | undefined>
        ) => {
            state.searchText = payload;
        },
        setProductDataLoading: (state, {payload}: PayloadAction<boolean>) => {
            state.productDataLoading = payload;
        },
        setIsRailOnFlat: (state, {payload}: PayloadAction<boolean>) => {
            state.isRailOnFlat = payload;
        },
        setIsRailOnEdge: (state, {payload}: PayloadAction<boolean>) => {
            state.isRailOnEdge = payload;
        },
        setCarcaseThickness: (state, {payload}: PayloadAction<number>) => {
            state.carcaseThickness = payload;
        },
        setRailWidth: (state, {payload}: PayloadAction<number>) => {
            state.railWidth = payload;
        },
        setApplianceOpeningHeight: (
            state,
            {payload}: PayloadAction<number>
        ) => {
            state.applianceOpeningHeight = payload;
        },
        setProductType: (state, {payload}: PayloadAction<number>) => {
            state.productType = payload;
        },
        setProductUpdated: (state, {payload}: PayloadAction<boolean>) => {
            state.productUpdated = payload;
        },
    },
});

export const isProductDataLoadingState = (store: AppState) =>
    store.productData.productDataLoading;
export const selectApplianceOpeningHeight = (store: AppState) =>
    store.productData.applianceOpeningHeight;
export const selectProductType = (store: AppState) =>
    store.productData.productType;
export const selectProductUpdated = (store: AppState) =>
    store.productData.productUpdated;
export const selectSearchText = (store: AppState) =>
    store.productData.searchText;
export const selectSearchingProduct = (store: AppState) =>
    store.productData.searchingProduct;

export const {
    setProductDataLoading,
    setIsRailOnEdge,
    setIsRailOnFlat,
    setCarcaseThickness,
    setRailWidth,
    setApplianceOpeningHeight,
    setProductType,
    setProductUpdated,
    setSearchText,
    setSearchingProduct,
    addErrorPopover,
    removeErrorPopover,
    addPopover,
    removePopover,
} = productSlice.actions;

export const {selectErrorPopovers, selectPopovers} = productSlice.selectors;

export default productSlice.reducer;
